import * as React from "react";
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useMemo, useState } from "react";
import { DateTime } from "luxon";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, Menu, MenuItem, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import { MoreVert } from "@mui/icons-material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import { getComparator, Order, stableSort } from "../../utils/Table";
import RefreshIcon from "@mui/icons-material/Refresh";
import { INeighborhood } from "services/backend/NeighborhoodService";

type NeighborhoodsTableHeadProperties = keyof INeighborhood | "totalPlayers";

interface HeadCell {
  id: NeighborhoodsTableHeadProperties;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
    padding: "none",
  },
  {
    id: "name",
    label: "Name",
  },
  {
    id: "totalPlayers",
    label: "Total Players",
    align: "right",
  },
  {
    id: "creationDate",
    label: "Creation Date",
    align: "right",
  },
  {
    id: "serverId",
    label: "Server ID",
    align: "right",
  },
];

interface NeighborhoodsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: NeighborhoodsTableHeadProperties) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const NeighborhoodsTableHead = ({ order, orderBy, onRequestSort }: NeighborhoodsTableHeadProps) => {
  const createSortHandler = (property: NeighborhoodsTableHeadProperties) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="normal" />
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell padding="normal" />
      </TableRow>
    </TableHead>
  );
};

interface NeighborhoodsTableToolbarProps {
  onRefreshButtonClick: () => void;
  filterQuery: string;
  onFilterQueryChange: (value: string) => void;
}

const NeighborhoodsTableToolbar = ({
  onRefreshButtonClick,
  filterQuery,
  onFilterQueryChange,
}: NeighborhoodsTableToolbarProps) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick} style={{ marginRight: 2 }}>
        Refresh
      </Button>
      <TextField
        id="outlined-search"
        label="Search by Name ,ID or Server ID"
        type="search"
        size="small"
        onChange={(e) => onFilterQueryChange(e.target.value)}
        sx={{ minWidth: "30%" }}
        value={filterQuery}
      />
    </Toolbar>
  );
};

export interface NeighborhoodsTableProps {
  rows: INeighborhood[];
  selectedItem: INeighborhood | undefined;
  setSelectedItem: Dispatch<SetStateAction<INeighborhood | undefined>>;
  onRefreshButtonClick: () => void;
  onDeleteMenuClick: () => void;
}

export const NeighborhoodsTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onRefreshButtonClick,
  onDeleteMenuClick,
}: NeighborhoodsTableProps) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<NeighborhoodsTableHeadProperties>("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);
  const [filterQuery, setFilterQuery] = useState("");

  const requestSortHandler = (event: MouseEvent<unknown>, property: NeighborhoodsTableHeadProperties) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const menuOpenHandler = (event: MouseEvent<HTMLElement>, item: INeighborhood) => {
    setMenuAnchor(event.target as Element);
    setSelectedItem(item);
  };

  const menuCloseHandler = () => {
    setMenuAnchor(undefined);
  };

  const deleteMenuClickHandler = () => {
    onDeleteMenuClick();
    menuCloseHandler();
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredRows = useMemo(() => {
    return rows.filter(
      (t) =>
        t.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
        t.id.toLowerCase().includes(filterQuery.toLowerCase()) ||
        (t.serverId && t.serverId.toLowerCase().includes(filterQuery.toLowerCase())),
    );
  }, [rows, filterQuery]);

  const visibleRows = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return stableSort(filteredRows as any, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <NeighborhoodsTableToolbar
          onRefreshButtonClick={onRefreshButtonClick}
          filterQuery={filterQuery}
          onFilterQueryChange={setFilterQuery}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <NeighborhoodsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={requestSortHandler}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `neighborhoods-table-checkbox-${index}`;
                const creationDate = row.creationDate
                  ? DateTime.fromMillis(row.creationDate).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                  : "unknown";
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index} sx={{ cursor: "pointer" }}>
                    <TableCell padding="normal" />
                    <TableCell component="th" id={labelId} scope="row" padding="none" style={{ fontFamily: "monospace" }}>
                      {row.id}
                    </TableCell>
                    <TableCell align="left">{row.name}</TableCell>
                    <TableCell align="right">{row.totalPlayers}</TableCell>
                    <TableCell align="right">{creationDate}</TableCell>
                    <TableCell align="right">{row.serverId || "N/A"}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => menuOpenHandler(e, row)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={pageChangeHandler}
          onRowsPerPageChange={rowsPerPageChangeHandler}
        />
        <Menu anchorEl={menuAnchor} keepMounted open={menuAnchor !== undefined} onClose={() => menuCloseHandler()}>
          <MenuItem onClick={() => deleteMenuClickHandler()} disabled={selectedItem === undefined}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Delete</Typography>
          </MenuItem>
        </Menu>
      </Paper>
    </Box>
  );
};
